import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { DevicePhoneMobileIcon, ShieldCheckIcon, CheckIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { localizeUserFacingError } from '../../utils/localizedError';
import { authService, AuthRequestError } from '../../services/auth';
import { finishQrLogin } from '../../services/qrLoginCompletion';
import styles from './AppQrLogin.module.css';

/** 两端使用同一状态呈现；只有服务端确认/兑换结果可触发登录动画。 */
export function AppQrLogin({ onBack, onSuccess, onBusyChange, onDisabled }: { onBack: () => void; onSuccess?: () => void; onBusyChange?: (busy: boolean) => void; onDisabled?: () => void }) {
  // 语言只影响当前呈现，不进入二维码创建与轮询 effect 的依赖。
  useSyncExternalStore(listener => i18nService.subscribe(listener), () => i18nService.getLanguage());
  const t = (key: string) => i18nService.t(key);
  const [round, setRound] = useState(0);
  const [ticket, setTicket] = useState<Record<string, unknown> | null>(null);
  const [state, setState] = useState('creating');
  const [message, setMessage] = useState('authAppQrCreating');
  const [rawError, setRawError] = useState<unknown>(null);
  const disabled = useRef(onDisabled); disabled.current = onDisabled;
  const success = useRef(onSuccess); success.current = onSuccess;
  const busy = useRef(onBusyChange); busy.current = onBusyChange;
  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let active: Record<string, unknown> | null = null;
    let failures = 0;
    let exchanging = false;
    const origin = authService.getBaseUrl();
    setState('creating'); setTicket(null); setRawError(null); setMessage('authAppQrCreating');
    /** 清理只向原始平台撤销；绝不把旧 owner 发往新平台。 */
    const cancel = () => {
      if (active && authService.getBaseUrl() === origin) void authService.appQrRequest('cancel', { code: active.code, owner: active.owner }).catch(() => {});
    };
    /** 功能关闭是终态，不能当作网络异常重试，也不能触发全局退出。 */
    const handleDisabled = (error: unknown): boolean => {
      if (!(error instanceof AuthRequestError) || error.code !== 'APP_QR_LOGIN_DISABLED') return false;
      clearTimeout(timer); cancel(); active = null;
      setState('disabled'); setRawError(null); setMessage('authAppQrDisabled');
      busy.current?.(false); disabled.current?.();
      return true;
    };
    /** 串行轮询，正常 800ms；临时网络故障退避，兑换永远不自动重放。 */
    const poll = async (): Promise<void> => {
      if (disposed || !active) return;
      if (authService.getBaseUrl() !== origin) { setState('invalid'); setMessage('authAppQrPlatformChanged'); return; }
      if (Date.now() >= Number(active.expires_at) * 1000) { setState('expired'); setMessage('authAppQrExpired'); cancel(); return; }
      try {
        const credentials = { code: active.code, owner: active.owner };
        const data = await authService.appQrRequest('status', credentials);
        if (disposed) return;
        if (authService.getBaseUrl() !== origin) { setState('invalid'); setMessage('authAppQrPlatformChanged'); return; }
        failures = 0;
        if (data.status === 'confirmed') {
          exchanging = true; busy.current?.(true); setState('logging'); setMessage('authAppQrLogging');
          const payload = await authService.appQrRequest('exchange', credentials);
          active = null;
          if (disposed || authService.getBaseUrl() !== origin) return;
          // 登录持久化/初始化完成才显示成功；组件卸载不取消独立的幂等回执。
          try {
            await finishQrLogin({
              request: (action, body) => authService.appQrRequest(action, body),
              initialize: data => authService.completeAppQrLogin(data),
              isCurrent: () => authService.getBaseUrl() === origin,
            }, String(credentials.code), payload);
            if (!disposed && authService.getBaseUrl() === origin) {
              setState('success'); setMessage('authAppQrSuccess');
              timer = setTimeout(() => { if (!disposed) success.current?.(); }, 1200);
            }
          } catch {
            if (!disposed) { setState('invalid'); setMessage('authAppQrInitFailed'); }
          } finally { if (!disposed) busy.current?.(false); }
          return;
        }
        const next = String(data.status);
        if (!['pending', 'scanned', 'verifying', 'deciding'].includes(next)) { setState('invalid'); setMessage('authAppQrCancelled'); return; }
        setState(next); setMessage(next === 'pending' ? 'authAppQrPending' : next === 'scanned' ? 'authAppQrScanned' : next === 'verifying' ? 'authAppQrVerifying' : 'authAppQrDeciding');
        timer = setTimeout(() => void poll(), 800);
      } catch (error) {
        if (disposed) return;
        if (handleDisabled(error)) return;
        if (exchanging) { setState('invalid'); setRawError(error); setMessage('authAppQrUnconfirmed'); busy.current?.(false); return; }
        if (error instanceof Error && /二维码.*(失效|过期|取消|使用)|不支持的登录/.test(error.message)) { setState('invalid'); setMessage('authAppQrInvalid'); return; }
        // 暂时网络错误不刷新 QR，也不自行进入已登录状态。
        failures++; setMessage('authAppQrRetrying');
        timer = setTimeout(() => void poll(), Math.min(5000, 800 * 2 ** Math.min(failures, 3)));
      }
    };
    void (async () => {
      try {
        active = await authService.appQrRequest('create');
        if (disposed) { cancel(); return; }
        if (!/^bomate-login:v1:[a-f0-9]{64}$/.test(String(active.qr_content)) || !/^[a-f0-9]{64}$/.test(String(active.owner)) || !Number.isFinite(Number(active.expires_at))) throw new Error('INVALID_QR_TICKET');
        setTicket(active); await poll();
      } catch (error) { if (!disposed && !handleDisabled(error)) { setState('invalid'); setRawError(error); setMessage('authAppQrUnavailable'); } }
    })();
    return () => { disposed = true; clearTimeout(timer); cancel(); busy.current?.(false); };
  }, [round]);
  const terminal = state === 'expired' || state === 'invalid' || state === 'disabled';
  const working = ['logging', 'deciding', 'success'].includes(state);
  const scanned = state === 'scanned';
  const Icon = state === 'verifying' ? ShieldCheckIcon : state === 'success' ? CheckIcon : terminal ? XMarkIcon : DevicePhoneMobileIcon;
  const remaining = ticket ? Math.max(1, Math.ceil((Number(ticket.expires_at) * 1000 - Date.now()) / 60000)) : 5;
  return <section className={styles.panel} data-state={state} aria-label={t('authAppQrRegion')} aria-busy={working}>
    {(scanned || working) && <span key={'beam-' + state} className={styles.beam + (state === 'logging' || state === 'deciding' ? ' ' + styles.receiving : '')} aria-hidden="true" />}
    <div key={terminal ? 'terminal' : state} className={styles.content}>
      {state === 'pending' && <h3 className={styles.title}>{t('authAppQrTitle')}</h3>}
      <div className={styles.code}>
        {ticket && ['pending', 'scanned', 'expired', 'invalid'].includes(state) && <div className={state === 'pending' ? '' : styles.blurred}><QRCodeSVG value={String(ticket.qr_content)} size={150} bgColor="#fff" fgColor="#111" marginSize={0} /></div>}
        {state === 'pending' && <span className={styles.scan} aria-hidden="true" />}
        {['creating', 'logging', 'deciding'].includes(state) ? <ArrowPathIcon className={styles.spinner} aria-hidden="true" /> : state !== 'pending' && <span className={styles.badge + (terminal ? ' ' + styles.failed : state === 'success' ? ' ' + styles.success : '')}><Icon aria-hidden="true" /></span>}
      </div>
      <div role="status" aria-live="polite" className={state === 'pending' ? styles.waiting : styles.title}>{state === 'pending' && <span className={styles.dot} aria-hidden="true" />}{rawError ? localizeUserFacingError(rawError, message) : t(message)}</div>
      <p className={styles.description}>{state === 'pending' ? <>
        {t('authAppQrInstructions')}<br />
        {t('authAppQrExpiryHint').replace('{minutes}', String(remaining))}
      </> : scanned ? t('authAppQrConfirmHint') : state === 'verifying' ? t('authAppQrVerifyHint')
        : state === 'logging' ? t('authAppQrLoggingHint')
        : state === 'success' ? t('authAppQrSuccessHint').replace('{app}', 'BoMate')
        : state === 'deciding' ? t('authAppQrDecidingHint') : ''}</p>
      {(scanned || state === 'verifying') && <span className={styles.dots} aria-hidden="true"><i /><i /><i /></span>}
      {ticket && !terminal && !working && <p className={styles.check}>{t('authAppQrCheckCode').replace('{code}', String(ticket.check_code))}</p>}
    </div>
    {terminal && <div className={styles.actions}><button type="button" onClick={onBack}>{t('authAppQrBack')}</button>{state !== 'disabled' && <button type="button" className={styles.primary} onClick={() => setRound(value => value + 1)}>{state === 'expired' ? t('authAppQrRefresh') : t('authAppQrRegenerate')}</button>}</div>}
  </section>;
}
